import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { Empty as EmptyCards } from '@sorare/core/src/components/cards/Empty';
import {
  useBackendPagination,
  useFiltersCount,
  useGQLSearchContext,
} from '@sorare/core/src/contexts/graphql-search';

import { BackendNoResults } from '#src/components/market/Empty/NoResults/index.tsx';
import {
  GridOverlayLoadingIndicator,
  MarketGrid,
} from '#src/components/market/Grid/index.tsx';
import { MatchdayPickCard } from '#src/components/matchdayPicks/MatchdayPickCard/index.tsx';
import type { Props as PlayerDrawerProps } from '#src/components/player/PlayerDrawer/index.tsx';
import { PlayerDrawer } from '#src/components/player/PlayerDrawer/index.tsx';
import type { MatchdayPick } from '#src/hooks/primaryBuy/useMatchdayPicks.ts';

const messages = defineMessages({
  noSale: {
    defaultMessage: 'No live pick right now. Check back on the next matchday.',
  },
  noMatch: {
    defaultMessage: 'No pick matches your filters.',
  },
  notOnSaleYet: {
    defaultMessage: "This matchday's picks aren't on sale yet.",
  },
  tryAgain: {
    defaultMessage: 'Try again',
  },
});

type Props = {
  picks: MatchdayPick[];
  liveCampaignCount: number;
  loading: boolean;
  onRetry: () => Promise<unknown>;
};

export const MatchdayPicksGrid = ({
  picks,
  liveCampaignCount,
  loading,
  onRetry,
}: Props) => {
  const { page, pages, refine } = useBackendPagination();
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);
  const { activeFacets } = useGQLSearchContext()!;
  const filtersCount = useFiltersCount();

  // `useFiltersCount` leaves `ff` out because favorites is a segment tab rather than a filter pill.
  // It still narrows the grid, so it counts when naming the reason a page came back empty.
  const favoritesOnly = !!activeFacets.ff?.length;
  const narrowed = filtersCount > 0 || favoritesOnly;

  if (loading && !picks.length) {
    return <LoadingIndicator fullHeight />;
  }

  if (!picks.length) {
    if (!liveCampaignCount) {
      return (
        <EmptyCards
          title={<FormattedMessage {...messages.noSale} />}
          description={null}
        />
      );
    }

    // A campaign with nothing on sale and nothing sold yet resolves to no card, so the search never
    // sees it and `nbHits` cannot tell an unopened sale from filters that match nothing. Whether the
    // user narrowed the grid at all is the only signal left that separates the two.
    if (narrowed) {
      return (
        <EmptyCards
          title={<FormattedMessage {...messages.noMatch} />}
          description={<BackendNoResults />}
        />
      );
    }

    return (
      <EmptyCards
        title={<FormattedMessage {...messages.notOnSaleYet} />}
        description={
          <Button color="tertiary" size="medium" onClick={onRetry}>
            <FormattedMessage {...messages.tryAgain} />
          </Button>
        }
      />
    );
  }

  return (
    <Vertical gap={2}>
      <div className="relative">
        <MarketGrid>
          {picks.map(pick => (
            <MatchdayPickCard
              key={pick.id}
              campaign={pick}
              onInfoClick={setCardOpened}
            />
          ))}
        </MarketGrid>
        {!!loading && <GridOverlayLoadingIndicator />}
      </div>
      <Pagination currentPage={page - 1} pages={pages} onSelect={refine} />
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
      />
    </Vertical>
  );
};
